import { Link } from "react-router-dom";
import { User } from "lucide-react";
import { SkillBadge } from "@/components/SkillBadge";
import { useTranslation } from "react-i18next";

interface Match {
  id: string;
  name: string;
  score: number;
  skills: string[];
}

interface TopMatchesCardProps {
  matches: Match[];
}

export const TopMatchesCard = ({ matches }: TopMatchesCardProps) => {
  const { t } = useTranslation();

  const getColorClass = (score) => {
    if (score <= 40) return "bg-red-100 text-red-600";
    if (score <= 69) return "bg-orange-100 text-orange-600";
    return "bg-green-100 text-green-600";
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-semibold mb-4">{t("jobs.topMatches", "Top Matches")}</h3>
      
      <div className="space-y-3">
        {matches.map(match => (
          <div key={match.id} className="border rounded-lg p-3">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-2">
                <User className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <Link to={`/candidates/${match.id}`} className="font-medium hover:underline">
                    {match.name}
                  </Link>
                  
                  {match.skills && match.skills.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {match.skills.slice(0, 2).map((skill, index) => (
                        <SkillBadge key={index} label={skill} />
                      ))}
                      {match.skills.length > 2 && (
                        <span className="text-xs text-gray-500">+{match.skills.length - 2} {t("common.more", "more")}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div className={`rounded-md flex items-center justify-center font-semibold ${getColorClass(match.score)}`}>
                {match.score}%
              </div>
              
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center">
        <Link to="/matches" className="text-digger-blue hover:underline text-sm">
          {t("dashboard.viewAllMatches", "View all matches")}
        </Link>
      </div>
    </div>
  );
};
